import { Component, OnInit } from '@angular/core';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { SavedSearchService } from '../../../services/saved-search.service';
import { DateTimeService } from '../../../services/date-time.service';
import { SavedSearch, ManageQueriesDialogData, SelectedQueryResult, RenameQueryDialogData, ManageQueriesDialogResult } from '../../../interfaces/saved-search.interface';
import { RenameQueryDialogComponent } from '../rename-query-dialog/rename-query-dialog.component';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

@Component({
  selector: 'app-manage-queries-dialog',
  templateUrl: './manage-queries-dialog.component.html',
  styleUrls: ['./manage-queries-dialog.component.less']
})
export class ManageQueriesDialogComponent implements OnInit {
  savedQueries: SavedSearch[] = [];
  loading = false;
  dialogData: ManageQueriesDialogData;
  dashboardId: string;
  selectedQueryId?: number;  // Track currently selected query from parent
  deletedSelectedQuery = false;  // Track if selected query was deleted
savedQueryForm : UntypedFormGroup;
  // Pagination
  page = 0;
  size = 10;
  totalElements = 0;
  gotoPageNumber: number | null = null;

  // Sorting
  sortKey: string = 'createdDate,desc';

  get maxPages(): number {
    return Math.ceil(this.totalElements / this.size) || 1;
  }

  get pageIndex(): number {
    return this.page + 1;
  }

  constructor(
    private modalRef: NzModalRef,
    private modalService: NzModalService,
    private savedSearchService: SavedSearchService,
    private dateTimService: DateTimeService,
    private message: NzMessageService,
    private fb: UntypedFormBuilder,
  ) {
    this.dialogData = this.modalRef.getConfig().nzData || {};
    this.dashboardId = this.dialogData.dashboardId || this.savedSearchService.getDashboardIdFromRoute();
    this.selectedQueryId = this.dialogData.selectedQueryId;    
    this.savedQueryForm = this.fb.group({
      queryName: [""],     
    });  
  }

  ngOnInit(): void {
    this.loadSavedQueries();
  }

  loadSavedQueries(): void {
    this.loading = true;
    console.log("calling api from here");
    this.savedSearchService.getAllSavedSearches(this.dashboardId, this.page, this.size,this.savedQueryForm?.value?.queryName,this.sortKey).subscribe({
      next: (response: any) => {
        // Handle different API response structures
        let dataArray: any[] = [];
        let total = 0;

        if (Array.isArray(response)) {
          dataArray = response;
          total = response.length;
        } else if (response?.content?.content) {
          // Nested structure: { content: { content: [...], totalElements: N } }
          dataArray = response.content.content;
          total = response.content.totalElements || dataArray.length;
        } else if (response?.content && Array.isArray(response.content)) {
          // Direct pageable: { content: [...], totalElements: N }
          dataArray = response.content;
          total = response.totalElements || dataArray.length;
        } else if (response?.data?.content) {
          // Data wrapper with pageable: { data: { content: [...] } }
          dataArray = response.data.content;
          total = response.data.totalElements || dataArray.length;
        } else if (response?.data && Array.isArray(response.data)) {
          dataArray = response.data;
          total = response.totalElements || dataArray.length;
        }

        // Map 'id' to 'saveSearchId' if needed
        this.savedQueries = dataArray.map((item: any) => ({
          ...item,
          saveSearchId: item.saveSearchId || item.id
        }));
        this.totalElements = total;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.message.error('Failed to load saved queries');
        console.error('Error loading saved queries:', error);
      }
    });
  }

  onPageChange(page: number): void {
    this.page = page - 1; // API is 0-indexed
    this.loadSavedQueries();
  }

  onPageSizeChange(size: number): void {
    this.size = size;
    this.page = 0;
    this.loadSavedQueries();
  }

  goToPage(): void {
    if (this.gotoPageNumber && this.gotoPageNumber >= 1 && this.gotoPageNumber <= this.maxPages) {
      this.page = this.gotoPageNumber - 1; // API is 0-indexed
      this.loadSavedQueries();
      this.gotoPageNumber = null;
    }
  }

  sort(sortEvent: any): void {
    if (sortEvent.key && sortEvent.value) {
      const direction = sortEvent.value === 'descend' ? 'desc' : sortEvent.value === 'ascend' ? 'asc' : sortEvent.value;
      this.sortKey = sortEvent.key + ',' + direction;
      this.loadSavedQueries();
    }
  }

  onApplyQuery(query: SavedSearch): void {
    const selectedQuery: SelectedQueryResult = {
      searchCriteria: this.savedSearchService.parseSearchCriteria(query.searchCriteria),
      configuredColumns: query.configuredColumns || [],
      queryName: query.queryName,
      saveSearchId: query.saveSearchId!,
      isDefault: query.isDefault
    };
    const result: ManageQueriesDialogResult = {
      action: 'apply',
      query: selectedQuery
    };
    this.modalRef.close(result);
  }

  formatDate(dateStr: string | undefined): string {
    if (!dateStr) return '-';
    return this.dateTimService.formatDateWithTime(dateStr);
  }

  onRename(query: SavedSearch): void {
    const renameData: RenameQueryDialogData = {
      currentName: query.queryName,
      queryId: query.saveSearchId!,
      dashboardId: this.dashboardId
    };

    const modal = this.modalService.create({
      nzTitle: 'Rename Query',
      nzContent: RenameQueryDialogComponent,
      nzData: renameData,
      nzWidth: 400,
      nzFooter: null
    });

    modal.afterClose.subscribe((result: boolean) => {
      if (result) {
        this.loadSavedQueries();
      }
    });
  }

  onSetDefault(query: SavedSearch): void {
    this.savedSearchService.setDefaultSavedSearch(query.saveSearchId!, this.dashboardId).subscribe({
      next: () => {
        this.message.success(`"${query.queryName}" set as default`);
        this.loadSavedQueries();
      },
      error: (error) => {
        this.message.error(error.error?.message || 'Failed to set default query');
      }
    });
  }

  onDelete(query: SavedSearch): void {
    this.modalService.confirm({
      nzTitle: 'Delete Query',
      nzContent: `Are you sure you want to delete the query "${query.queryName}"?`,
      nzOkText: 'Delete',
      nzOkDanger: true,
      nzCancelText: 'Cancel',
      nzOnOk: () => {
        this.savedSearchService.deleteSavedSearch(query.saveSearchId!, this.dashboardId).subscribe({
          next: () => {
            this.message.success('Query deleted successfully');
            // Track if the deleted query was the selected one
            if (this.selectedQueryId && query.saveSearchId === this.selectedQueryId) {
              this.deletedSelectedQuery = true;
              // Immediately close dialog and return deleted action
              const result: ManageQueriesDialogResult = {
                action: 'deleted',
                deletedQueryId: this.selectedQueryId
              };
              this.modalRef.close(result);
            }
            this.loadSavedQueries();
          },
          error: (error) => {
            this.message.error(error.error?.message || 'Failed to delete query');
          }
        });
      }
    });
  }

  onClose(): void {
    // If the selected query was deleted, inform the parent
    if (this.deletedSelectedQuery && this.selectedQueryId) {
      const result: ManageQueriesDialogResult = {
        action: 'deleted',
        deletedQueryId: this.selectedQueryId
      };
      this.modalRef.close(result);
    } else {
      const result: ManageQueriesDialogResult = {
        action: 'close'
      };
      this.modalRef.close(result);
    }
  }

  searchSubmit(){
    this.loadSavedQueries();
  }

  resetForm(){
    this.savedQueryForm.reset();
    this.loadSavedQueries();
  }
}
